<script setup lang="ts">
import { computed } from 'vue'
import DatePicker from 'primevue/datepicker'
import { fromLocalDateInput, toLocalDateInput } from '@/utils/format'

/**
 * Campo de fecha con formato fijo `dd/mm/aaaa`.
 *
 * Antes era `<input type="date">`, y ese control **no** decide su formato la página:
 * lo decide el idioma del navegador. El mismo código mostraba `01/10/2026` en un
 * equipo en español y `10/01/2026` en uno en inglés, con el resto de las fechas de la
 * aplicación en un formato fijo. Dos formatos según el equipo, para cuatro campos.
 *
 * Este componente pone el formato en el componente, así que se ve igual en todos.
 *
 * **El valor sigue siendo la cadena `YYYY-MM-DD` de siempre.** Es lo que viaja a la
 * URL, lo que lee el backend y lo que guardan los filtros; cambiarlo obligaría a
 * tocar el backend y las tres vistas para acabar exactamente igual. El adaptador es
 * entero: entra la cadena, sale la `Date` del selector, y vuelve a salir la cadena.
 *
 * El intercambio entero es lo que evita el desfase de un día: `new Date('2026-10-01')`
 * es la medianoche **en UTC**, y al pintarlo al oeste de UTC sale el 30 de septiembre.
 */

/**
 * El formato del selector de calendario.
 *
 * Ojo con los años: en PrimeVue **cada `y` es un dígito**, no una cosa que se
 * repita. `dd/mm/yyyy` no da `01/10/2026`, da `01/10/20262026`: cuatro `y` son ocho
 * dígitos. Para cuatro cifras hay que escribir `dd/mm/yy`.
 *
 * Es justo lo contrario que en `Intl` o en date-fns, donde `yyyy` es el token y `yy` el
 * año corto. Copiar el formato de `formatShortDate` tal cual da un año de ocho cifras
 * que ocupa el doble de hueco y parece un error de carga.
 */
const FORMATO = 'dd/mm/yy'

const props = withDefaults(
  defineProps<{
    /** Fecha en `YYYY-MM-DD`, o `''` si el campo está vacío. */
    modelValue: string
    inputId: string
    label?: string
    placeholder?: string
    /** Fecha mínima en `YYYY-MM-DD`. */
    min?: string
    /** Fecha máxima en `YYYY-MM-DD`. */
    max?: string
    /** Etiqueta accesible cuando no hay `label` visible. */
    ariaLabel?: string
  }>(),
  {
    label: undefined,
    placeholder: 'dd/mm/aaaa',
    min: undefined,
    max: undefined,
    ariaLabel: undefined,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const fecha = computed(() => toLocalDateInput(props.modelValue))

// `minDate` y `maxDate` de PrimeVue piden `undefined` y no `null`: `null` los tipa
// como error y `undefined` como «sin límite», que es justo lo que significa no
// haber puesto `min` o `max`.
const minimo = computed(() => toLocalDateInput(props.min) ?? undefined)
const maximo = computed(() => toLocalDateInput(props.max) ?? undefined)

/**
 * Recibe lo que emite el selector, que tipa un valor más ancho del que este campo
 * puede dar: con `selectionMode="single"` —el valor por defecto— llega una `Date` o
 * un vacío, y nunca una lista. El `Array.isArray` no es defensivo por costume: es lo
 * que le dice a TypeScript que la lista no puede aparecer, sin tener que vaciar el
 * tipado del evento.
 */
function alCambiar(value: Date | Date[] | (Date | null)[] | null | undefined): void {
  if (Array.isArray(value)) {
    // Solo alcanzable si alguien cambia `selectionMode` sin revisar esto. Se coje el
    // primero para no perder la fecha, y se deja escrito para que salte a la vista.
    emit('update:modelValue', fromLocalDateInput(value[0] ?? null))
    return
  }

  emit('update:modelValue', fromLocalDateInput(value ?? null))
}
</script>

<template>
  <label
    v-if="label"
    :for="inputId"
    class="label-base"
  >
    {{ label }}
  </label>

  <DatePicker
    :input-id="inputId"
    :model-value="fecha"
    :min-date="minimo"
    :max-date="maximo"
    :date-format="FORMATO"
    :placeholder="placeholder"
    :aria-label="ariaLabel ?? label"
    icon-display="input"
    show-icon
    class="w-full"
    input-class="w-full"
    @update:model-value="alCambiar"
  />
</template>